/**
 * profile.ts — the provider's own profile, loaded once and shared.
 * =====================================================================
 * The sidebar identity and the jobs cert-expiry banner both need the provider
 * profile (GET /api/v1/provider/profile/). We memoise the promise so it is
 * fetched at most once per page load and shared by every consumer.
 *
 * Populating the sidebar lives here (not in a screen component) because the
 * sidebar is part of the shell (index.html), outside the router outlet.
 */
import { getProviderProfile, type ProviderProfile } from "./api/client";

let profilePromise: Promise<ProviderProfile> | null = null;

/** Load the provider profile once; subsequent callers share the same promise. */
export function loadProfile(): Promise<ProviderProfile> {
  if (!profilePromise) profilePromise = getProviderProfile();
  return profilePromise;
}

/**
 * Certifications expiring within `days` (default 90) — drives the jobs
 * cert-expiry banner. Only verified certs with a real expiry date count; we never
 * fabricate a date, so certs without an `expiry_date` are silently skipped.
 */
export function certsExpiringSoon(
  profile: ProviderProfile,
  days = 90,
): Array<{ name: string; daysLeft: number }> {
  const out: Array<{ name: string; daysLeft: number }> = [];
  for (const c of profile.certifications ?? []) {
    if (!c.verified || !c.expiry_date) continue;
    const due = new Date(c.expiry_date).getTime();
    if (Number.isNaN(due)) continue;
    const daysLeft = Math.ceil((due - Date.now()) / 86_400_000);
    if (daysLeft >= 0 && daysLeft <= days) {
      out.push({ name: c.doc_type_display || c.doc_type || "Certification", daysLeft });
    }
  }
  return out.sort((a, b) => a.daysLeft - b.daysLeft);
}

/**
 * Fill the shell sidebar's identity block from the profile. Best-effort: if the
 * profile can't load (e.g. a user mid-onboarding with no Provider row), the
 * neutral placeholders in index.html simply remain.
 */
export async function populateSidebar(): Promise<void> {
  let profile: ProviderProfile;
  try {
    profile = await loadProfile();
  } catch {
    return; // leave the placeholders; the auth gate handles real auth failures
  }
  const company = document.querySelector<HTMLElement>(".sidebar-user .user-company");
  if (company) {
    // Preserve the Preferred badge node (a child) while setting the name text.
    const badge = company.querySelector<HTMLElement>(".preferred-badge");
    company.textContent = profile.company_name ?? "Mango provider";
    if (badge) {
      company.appendChild(badge);
      badge.hidden = !profile.is_preferred;
    }
  }
  const role = document.getElementById("sidebar-role-display");
  if (role) {
    const label = profile.role === "admin" ? "Admin" : "Staff";
    role.innerHTML = `<i class="ti ti-shield-check" style="font-size:11px"></i> ${label}`;
  }
}
